import { Dispatch, SetStateAction } from 'react'
import { AnnotationService } from '@amplify/react-epub-reader'
import {
  resetMarksClassList,
  hideMark,
  getElementByCFI,
  getCfiBodyId,
  createSolidHiglight,
  changeHighlightColor,
  getHighlightedSpansIds,
  preventUserSelection,
  isInSecondIframe,
} from 'app/services/utils/annotation.utils'
import { EREADER_SELECTION_COLORS } from 'app/constants/color-constants'
import {
  markSize,
  bookContainerSelector,
  ANNOTATION_EVENTS,
  ANNOTATION_TYPES,
} from 'app/constants/annotation.constants'
import {
  Annotation,
  AnnotationData,
  AnnotationsObject,
  AnnotationSetupOptions,
  GroupPosition,
  MarkArea,
  MarkItem,
} from 'app/types/annotation.types'

import positionService from './annotation-position.service'
import markGroupService from './mark-group.service'
import annotationApi from './annotation-save.service'

const defaultColor = EREADER_SELECTION_COLORS[0]

class AnnotationController {
  static service: AnnotationService
  static document: Document
  static popup: HTMLElement | null
  static setMarkText: Dispatch<SetStateAction<string>>
  static setMarkData: Dispatch<SetStateAction<MarkItem[]>>
  static setHighlightColor: Dispatch<SetStateAction<string>>
  static highlightColor: string = defaultColor
  static bookContainer: HTMLDivElement
  static markArea: MarkArea
  static isMiddleSchool: boolean
  static annotations: AnnotationsObject
  static highlightedSpans: Record<string, string | boolean>
  static cfi: string | null
  static originalCfi: string | null
  static isSelectHighlighted?: boolean
  static mainMarkPosition: GroupPosition | null
  static sendEvent: (event: string) => void
  static resetMarkSizeAndPosition: () => void
  static handleSelect: (cfi: string | null, document: Document) => void
  static handleHighlightedClick: (e: MouseEvent) => void

  static setupController({
    service,
    popupRef,
    sendEvent,
    resetMarkSizeAndPosition,
    setMarkText,
    setMarkData,
    setHighlightColor,
    isMiddleSchool,
  }: AnnotationSetupOptions) {
    AnnotationController.service = service
    AnnotationController.document = service.document
    AnnotationController.popup = popupRef.current
    AnnotationController.sendEvent = sendEvent
    AnnotationController.resetMarkSizeAndPosition = resetMarkSizeAndPosition
    AnnotationController.setMarkText = setMarkText
    AnnotationController.setMarkData = setMarkData
    AnnotationController.setHighlightColor = setHighlightColor
    AnnotationController.highlightColor = defaultColor
    AnnotationController.bookContainer = document.querySelector(
      bookContainerSelector
    ) as HTMLDivElement
    AnnotationController.markArea = markGroupService.buildMarkArea(
      AnnotationController.bookContainer
    )

    AnnotationController.isMiddleSchool = isMiddleSchool
    AnnotationController.annotations = {}
    AnnotationController.highlightedSpans = {}

    AnnotationController.handleSelect = isMiddleSchool
      ? AnnotationController.handleMiddleSchoolSelect
      : AnnotationController.handleK2Select

    AnnotationController.handleHighlightedClick = isMiddleSchool
      ? AnnotationController.handleMiddleSchoolHighlightedClick
      : AnnotationController.handleK2HighlightedClick
  }

  static getValueKey(cfi: string | null, type: string) {
    return `${cfi}---${type}`
  }

  static setAnnotation(annotation: Annotation) {
    const annotationKey = AnnotationController.getValueKey(
      annotation.cfi,
      annotation.type
    )
    AnnotationController.annotations[annotationKey] = annotation
    annotationApi.saveAnnotation(AnnotationController.annotations)
  }

  static removeAnnotation(
    cfi: string | null,
    type = ANNOTATION_TYPES.HIGHLIGHT
  ) {
    const key = AnnotationController.getValueKey(cfi, type)
    if (!AnnotationController.annotations[key]) return
    delete AnnotationController.annotations[key]

    annotationApi.removeAnnotation(AnnotationController.annotations)
  }

  static setHighlightedSpans(
    annotations: Record<string, unknown>,
    isRemoved?: boolean
  ) {
    const highlightedSpans = getHighlightedSpansIds(annotations, isRemoved)
    AnnotationController.highlightedSpans = {
      ...AnnotationController.highlightedSpans,
      ...highlightedSpans,
    }
  }

  static setCfi(cfi: string | null) {
    AnnotationController.cfi = cfi
  }

  static clearCfi() {
    AnnotationController.cfi = null
  }

  static copyText() {
    AnnotationController.service.document.execCommand('copy')
  }

  static removeMark(cfi: string | null) {
    const mark = getElementByCFI(cfi, 'a', AnnotationController.bookContainer)
    if (!mark) return

    const { top = '', left = 0 } = mark.dataset
    const bodyId = AnnotationController.document.body.id

    const areaTop = markGroupService.findClosestLine(
      +top,
      AnnotationController.markArea
    )
    const area = AnnotationController.markArea[areaTop][bodyId]

    if (area) {
      area.marks = area.marks.filter(
        (areaMark: MarkItem) => areaMark.cfi !== cfi
      )
      if (area.marks.length >= 1) {
        const markCfi = area.marks[0].cfi
        const { top: markTop } = positionService.getSettedMarkPosition(markCfi)
        const dataSetId = markGroupService.getMarkGroupDataId(bodyId, areaTop)

        const currentGroupMark = markGroupService.getCurrentGroupMark(dataSetId)
        markGroupService.updateNumberOfNotes(
          currentGroupMark,
          area.marks.length
        )

        // Remove multiple mark icon
        if (area.marks.length === 1) {
          markGroupService.removeGroupMark(currentGroupMark)
          AnnotationController.placeSingleMark(
            markCfi,
            { left: +left, top: markTop },
            areaTop,
            bodyId
          )
        }
      }
    }

    AnnotationController.removeAnnotation(cfi, ANNOTATION_TYPES.MARK)
    AnnotationController.service.removeMark(cfi)
  }

  static removeHighlight(cfi: string | null) {
    AnnotationController.removeAnnotation(cfi, ANNOTATION_TYPES.HIGHLIGHT)
    AnnotationController.service.removeHighlight(cfi)
  }

  static mark(cfi: string, data?: AnnotationData, saveToStorage = true) {
    if (saveToStorage) {
      AnnotationController.setAnnotation({
        cfi,
        data,
        type: ANNOTATION_TYPES.MARK,
      })
    }

    return AnnotationController.service.mark(
      cfi,
      data,
      AnnotationController.handleMarkClick.bind(this)
    )
  }

  static highlight(
    cfi: string | null,
    color = AnnotationController.highlightColor,
    saveToStorage = true
  ) {
    AnnotationController.service.highlight(
      cfi,
      {},
      AnnotationController.handleHighlightedClick.bind(this),
      undefined,
      { fill: color }
    )

    const annotation = {
      cfi: cfi ?? '',
      color,
      type: ANNOTATION_TYPES.HIGHLIGHT,
    }

    if (saveToStorage) AnnotationController.setAnnotation(annotation)

    const highlightElement = getElementByCFI(cfi, 'g')
    if (highlightElement) {
      createSolidHiglight(highlightElement)
    }

    AnnotationController.setHighlightedSpans({ annotation })
  }

  static handleHighlight() {
    AnnotationController.highlight(AnnotationController.cfi)
    AnnotationController.setPopupPosition()
    // do not send event if we moving draggers
    if (!AnnotationController.isSelectHighlighted) {
      AnnotationController.isSelectHighlighted = true
      AnnotationController.originalCfi = AnnotationController.cfi
      AnnotationController.sendEvent(ANNOTATION_EVENTS.HIHLIGHT)
    }
  }
  static handleK2HighlightedClick(e: MouseEvent) {
    const eventTarget = e.target as SVGGElement

    const { epubcfi } = eventTarget.dataset
    if (!epubcfi) return
    AnnotationController.handleAnnotationRemove(false, epubcfi)
  }

  static handleMiddleSchoolHighlightedClick(e: MouseEvent) {
    const eventTarget = e.target as SVGGElement

    const { epubcfi } = eventTarget.dataset
    if (!epubcfi) return
    AnnotationController.setCfi(epubcfi)
    AnnotationController.selectCfi(epubcfi)
    AnnotationController.isSelectHighlighted = true
    AnnotationController.originalCfi = epubcfi

    const fillColor = eventTarget.getAttribute('fill')
    if (fillColor) {
      AnnotationController.highlightColor = fillColor
      AnnotationController.setHighlightColor(fillColor)
    }

    AnnotationController.setPopupPosition()
    AnnotationController.sendEvent(ANNOTATION_EVENTS.HIGHLIGHT_CLICK)
  }

  static addMark(cfi: string | null, note: string) {
    if (!cfi) return
    const range = AnnotationController.service.getRange(cfi)
    const { left, middleY } = positionService.calculateMarkPosition(
      range,
      AnnotationController.document.body,
      AnnotationController.service.isSecondIframe
    )
    const markCenter = middleY - markSize / 2
    AnnotationController.mark(cfi, { note, top: markCenter, left })

    const bodyId = AnnotationController.document.body.id

    AnnotationController.placeMarkGroup(
      { cfi, note, top: markCenter, left },
      bodyId
    )
  }

  static openNote(mark: { cfi: string; note: string }) {
    AnnotationController.setCfi(mark.cfi)
    AnnotationController.setMarkText(mark.note)
    AnnotationController.sendEvent(ANNOTATION_EVENTS.MARK_CLICK)
  }

  static handleMarkClick(e: MouseEvent, groupPosition?: GroupPosition) {
    const eventTarget = e.target as HTMLElement

    AnnotationController.handleAnnotationOpen(e, true)
    let position = groupPosition

    if (!position) {
      const { epubcfi = '', note = '', top = '' } = eventTarget.dataset
      const { left } = positionService.getSettedMarkPosition(
        epubcfi,
        AnnotationController.bookContainer
      )
      AnnotationController.openNote({ cfi: epubcfi, note })
      position = { top, left }
    } else {
      const area =
        AnnotationController.markArea[position.top][position.id ?? '']
      if (!area) return
      AnnotationController.setMarkData(area.marks)
      AnnotationController.sendEvent(ANNOTATION_EVENTS.MARK_GROUP_CLICK)
    }

    if (!AnnotationController.mainMarkPosition && position) {
      AnnotationController.mainMarkPosition = position
    }

    eventTarget.classList.add('active')

    if (AnnotationController.mainMarkPosition && AnnotationController.popup) {
      positionService.placeMarkPopup(
        AnnotationController.mainMarkPosition,
        AnnotationController.popup
      )
    }
  }

  static handleNoteAddClick() {
    AnnotationController.setMarkData([])
    const markElement = getElementByCFI(
      AnnotationController.cfi,
      'a',
      AnnotationController.bookContainer
    )

    if (markElement?.dataset.note) {
      AnnotationController.setMarkText(markElement.dataset.note)
    }

    AnnotationController.setPopupPosition()
    AnnotationController.sendEvent(ANNOTATION_EVENTS.ADD_NOTE)
  }

  static handleNoteSave(note: string) {
    const { cfi } = AnnotationController
    const highlight = getElementByCFI(
      cfi,
      'g',
      AnnotationController.bookContainer
    )
    const mark = getElementByCFI(cfi, 'a', AnnotationController.bookContainer)

    if (mark) AnnotationController.removeMark(cfi)
    if (!highlight) AnnotationController.highlight(cfi)

    AnnotationController.addMark(cfi, note)
    AnnotationController.handlePopupClose()
    AnnotationController.sendEvent(ANNOTATION_EVENTS.SAVE)
  }

  static handleColorChange(color: string) {
    const { cfi } = AnnotationController

    AnnotationController.highlightColor = color
    AnnotationController.setHighlightColor(color)
    changeHighlightColor(cfi, color)
    AnnotationController.service.changeHighlightColor(cfi, color)
    AnnotationController.setAnnotation({
      cfi: cfi ?? '',
      color,
      type: ANNOTATION_TYPES.HIGHLIGHT,
    })
  }

  static handleAnnotationRemove(
    isMark: boolean,
    cfi = AnnotationController.cfi
  ) {
    AnnotationController.removeHighlight(cfi)
    if (isMark) AnnotationController.removeMark(cfi)

    AnnotationController.clearCfi()
    AnnotationController.handlePopupClose()
    AnnotationController.clearSelect()
    AnnotationController.sendEvent(ANNOTATION_EVENTS.DELETE)

    AnnotationController.setHighlightedSpans(
      {
        removedAnnotation: { cfi, type: ANNOTATION_TYPES.HIGHLIGHT },
      },
      true
    )
  }

  static handleAnnotationOpen(e: MouseEvent, isMark: boolean) {
    e.stopPropagation()
    AnnotationController.checkIframeOutsideClick(e.target as HTMLElement)
    if (isMark) resetMarksClassList()
  }

  static handlePopupClose() {
    AnnotationController.highlightColor = defaultColor
    AnnotationController.setHighlightColor(defaultColor)
    AnnotationController.setMarkText('')
    AnnotationController.mainMarkPosition = null
    AnnotationController.resetMarkSizeAndPosition()
  }

  static closePopup() {
    resetMarksClassList()
    AnnotationController.handlePopupClose()
    AnnotationController.clearSelect()
    AnnotationController.originalCfi = ''
    AnnotationController.isSelectHighlighted = false
    AnnotationController.sendEvent(ANNOTATION_EVENTS.CLICK_OUTSIDE)
  }

  static handleK2Select(
    cfi = AnnotationController.cfi,
    document = AnnotationController.document
  ) {
    AnnotationController.setCfi(cfi)
    AnnotationController.document = document

    AnnotationController.highlight(cfi)
  }

  static handleMiddleSchoolSelect(
    cfi = AnnotationController.cfi,
    document = AnnotationController.document
  ) {
    AnnotationController.setCfi(cfi)
    AnnotationController.document = document

    AnnotationController.setPopupPosition(cfi)

    // Change already highlighted select
    if (
      AnnotationController.isSelectHighlighted &&
      AnnotationController.originalCfi !== AnnotationController.cfi
    ) {
      if (AnnotationController.originalCfi) {
        const placedMark = getElementByCFI(
          AnnotationController.originalCfi,
          'a'
        )
        AnnotationController.removeHighlight(AnnotationController.originalCfi)

        if (placedMark) {
          AnnotationController.removeMark(AnnotationController.originalCfi)
          AnnotationController.addMark(
            AnnotationController.cfi,
            placedMark.dataset.note ?? ''
          )
        }

        AnnotationController.originalCfi = AnnotationController.cfi
      }
      AnnotationController.handleHighlight()
      return
    }

    AnnotationController.sendEvent(ANNOTATION_EVENTS.SELECTED)
  }

  static async setPopupPosition(cfi = AnnotationController.cfi) {
    AnnotationController.cfi = cfi

    // 'await' is required here for correct popup position set
    const range = await AnnotationController.service.getRange(
      AnnotationController.cfi
    )
    if (!range) return

    // Add offset if it second page
    const popupOffset = AnnotationController.service.isSecondIframe
      ? AnnotationController.service.iframes[0].clientWidth
      : 0

    if (!AnnotationController.popup) return

    positionService.placePopup(
      range,
      AnnotationController.popup,
      AnnotationController.document.body,
      popupOffset
    )
  }

  static selectCfi(cfi: string) {
    const selection = AnnotationController.document.getSelection()
    if (selection) {
      const range = AnnotationController.service.getRange(cfi)
      selection.empty()
      selection.addRange(range)
    }
  }

  static clearSelect() {
    const selection = AnnotationController.document.getSelection()
    if (selection) selection.removeAllRanges()
  }

  static onDraggersRemove() {
    AnnotationController.isSelectHighlighted = false
    AnnotationController.originalCfi = null
  }

  static onPageChange() {
    if (!AnnotationController.isMiddleSchool) {
      preventUserSelection()
    }
    AnnotationController.markArea = markGroupService.clearMarkArea(
      AnnotationController.markArea
    )

    const marks =
      AnnotationController.bookContainer.querySelectorAll<HTMLAnchorElement>(
        'a[ref="epubjs-mk"]'
      )
    marks.forEach((markEl) => AnnotationController.parsePageMark(markEl))
  }

  static checkIframeOutsideClick(target: HTMLElement) {
    const closestIframe = target
      .closest('.epub-view')
      ?.getElementsByTagName('iframe')?.[0]?.contentDocument

    if (
      closestIframe &&
      closestIframe !== AnnotationController.service.document
    ) {
      AnnotationController.service.setDocument(closestIframe)
      AnnotationController.document = closestIframe
    }
  }

  static parsePageMark(
    mark?:
      | HTMLElement
      | {
          cfi: string
          data?: {
            note: string
          }
        }
  ) {
    const cfi = mark && 'cfi' in mark ? mark.cfi : mark?.dataset?.epubcfi
    const note = mark && 'cfi' in mark ? mark.data?.note : mark?.dataset?.note

    const bodyId = getCfiBodyId(cfi, [...AnnotationController.service.iframes])
    if (!bodyId) return

    const top =
      mark && 'style' in mark && mark?.style?.top
        ? parseFloat(mark.style.top)
        : positionService.getSettedMarkPosition(cfi).top
    const left = positionService.calculateMarkLeft(cfi ?? '')

    AnnotationController.placeMarkGroup(
      { cfi: cfi ?? '', note: note ?? '', top, left },
      bodyId
    )
  }

  static placeSingleMark(
    cfi: string,
    position: { top: number; left: number },
    areaTop: number,
    bodyId: string
  ) {
    let markTop = position.top

    const overlap = markGroupService.findOverlap(
      markTop,
      areaTop,
      AnnotationController.markArea,
      bodyId
    )

    if (overlap) markTop += overlap

    positionService.placeMark(
      cfi,
      { left: position.left, top: markTop },
      AnnotationController.bookContainer
    )
  }

  static placeMarkGroup(
    {
      cfi,
      note,
      top,
      left,
    }: {
      cfi: string
      note: string
      top: number
      left: number
    },
    bodyId: string
  ) {
    const areaTop = markGroupService.findClosestLine(
      top,
      AnnotationController.markArea
    )

    if (!AnnotationController.markArea[areaTop][bodyId]) {
      AnnotationController.markArea[areaTop][bodyId] = { marks: [] }
    }

    const area = AnnotationController.markArea[areaTop][bodyId]
    area.marks.push({ cfi, note })

    // Add single mark
    if (area.marks.length === 1) {
      AnnotationController.placeSingleMark(cfi, { left, top }, areaTop, bodyId)
      return
    }

    const groupId = markGroupService.getMarkGroupDataId(bodyId, areaTop)

    const currentGroupMark = markGroupService.getCurrentGroupMark(groupId)

    if (currentGroupMark) {
      markGroupService.updateNumberOfNotes(currentGroupMark, area.marks.length)
      return
    }

    if (area.marks.length === 2 || !currentGroupMark) {
      if (!area.id) area.id = bodyId

      const overlap = markGroupService.findGroupOverlap(
        String(areaTop),
        AnnotationController.markArea,
        bodyId
      )

      // Move single mark out of group dimension
      if (overlap) {
        const { top: overlapTop, left: overlapLeft } =
          positionService.getSettedMarkPosition(overlap.markCfi)

        positionService.placeMark(
          overlap.markCfi,
          { left: overlapLeft, top: overlapTop + overlap.distance },
          AnnotationController.bookContainer
        )
      }

      // Hide other marks in area
      area.marks.forEach((areaMark: MarkItem) => hideMark(areaMark.cfi))

      const isRight = isInSecondIframe(cfi)

      markGroupService.addGroupMark(
        { left, top: String(areaTop), id: bodyId, cfi },
        AnnotationController.handleMarkClick.bind(this),
        isRight,
        AnnotationController.markArea[areaTop]?.[bodyId]?.marks?.length
      )
    }
  }

  static handleWindowResize() {
    if (
      AnnotationController.service.isSecondIframe &&
      AnnotationController.service.iframes.length === 1
    ) {
      AnnotationController.service.isSecondIframe = false
    }
  }

  static async loadAnnotationsFromStore() {
    const annotations: AnnotationsObject =
      await annotationApi.getAllAnnotations()
    AnnotationController.annotations = annotations
    AnnotationController.setHighlightedSpans(annotations)

    // G5_U07_spanishRDR_2ndED19_20190711_EPUB3-8
    const bookId = AnnotationController.document.body.id
    const bookIdParsed = bookId.substring(0, bookId.indexOf('-'))

    Object.values(annotations).forEach((annotation) => {
      const annotationContainsBookId = annotation.cfi?.includes(bookIdParsed)
      if (!annotationContainsBookId) return
      if (annotation.type === ANNOTATION_TYPES.HIGHLIGHT) {
        AnnotationController.highlight(annotation.cfi, annotation.color, false)
      }
      if (annotation.type === ANNOTATION_TYPES.MARK) {
        AnnotationController.mark(annotation.cfi ?? '', annotation.data, false)
        AnnotationController.parsePageMark(annotation)
      }
    })

    return annotations
  }
}

export const annotationController = AnnotationController
